import {
  ADD_CONTACT_SUCCESS,
  BULK_SAVE_CONTACTS_COMPLETED,
  CANCEL_CONTACT_INLINE_EDIT,
  DELETE_CONTACT_SUCCESS,
  GET_COMPANY_CONTACTS_SUCCESS,
  GET_CONTACTS_SUCCESS_UNBATCHED,
  GET_CONTACT_SUCCESS,
  GET_REMINDERS_SUCCESS,
  UPDATE_CONTACT_CUSTOM_FIELDS_SUCCESS,
  UPDATE_CONTACT_PROPERTY,
  UPDATE_CONTACT_SUCCESS,
} from 'actions';
import language from 'config/language';
import { EntityType } from 'enums';
import { ICustomField } from 'interfaces';
import { get as _get, pick as _pick } from 'lodash-es';
import Company from 'models/Company';
import { Model, ModelType, attr, fk } from 'redux-orm';

import { getBracketByScore } from '../utils/status';

export interface IContact {
  id: string;
  accountId: string;
  companyId: Company;
  firstName: string;
  lastName: string;
  isArchived: boolean;
  role: string;
  roleType: string;
  emails: Array<string>;
  phone: Array<string>;
  timestamp: string;
  relationship: number;
  entityType: EntityType;
  referrerId?: Contact;
  hasReferred?: string[];
  customFields: ICustomField[];
  usersRelationship?: number;
  timeGroupedInteractionStats?: unknown;
  dirtyFields: {
    [key: string]: any;
  } | null;
}

type ContactUpsert = Omit<IContact, 'companyId' | 'referrerId'>;

class Contact extends Model<typeof Contact, IContact> {
  id!: string;

  accountId!: string;

  companyId!: Company;

  firstName!: string;

  lastName!: string;

  isArchived!: boolean;

  role!: string;

  roleType!: string;

  emails!: Array<string>;

  phone!: Array<string>;

  timestamp!: string;

  relationship!: number;

  entityType!: EntityType;

  referrerId!: Contact;

  customFields!: ICustomField[];

  usersRelationship?: number;

  static reducer(action: any, model: ModelType<Contact>) {
    // eslint-disable-next-line default-case
    switch (action.type) {
      case GET_COMPANY_CONTACTS_SUCCESS: {
        action.data.forEach(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ({ company, ...contact }: { company: any } & ContactUpsert) => {
            const current = model.withId(contact.id);
            if (!current) {
              model.upsert(contact);
            }
            // Only update an existing company if it is not already archived
            else if (!current.isArchived) {
              model.upsert(contact);
            }
          },
        );

        break;
      }
      case GET_CONTACTS_SUCCESS_UNBATCHED:
        action.data.forEach((contact: ContactUpsert & { company: unknown }) => {
          /**
           * Archived companyies may still have contacts in the BE data.
           * This will show up in the UI as AGgrid rows disappearing on the
           * contacts page when sorting by column.  So only add contacts
           * if they have a company object.
           */
          if (contact && contact.company) {
            model.upsert(contact);
          }
        });
        break;
      case ADD_CONTACT_SUCCESS:
      case GET_CONTACT_SUCCESS:
        model.upsert(action.data);
        break;

      case UPDATE_CONTACT_CUSTOM_FIELDS_SUCCESS:
      case UPDATE_CONTACT_SUCCESS:
        model.upsert(action.data);
        break;

      // These changes are local edits - they have not been saved to the server
      case UPDATE_CONTACT_PROPERTY:
        model.upsert(action.data);
        break;

      // When inline edit is cancelled we need to remove all dirty values
      case BULK_SAVE_CONTACTS_COMPLETED: {
        const edited = model.filter((company) => !!company.dirtyFields);
        edited.toModelArray().forEach((company) => {
          company.update({
            ...company.dirtyFields,
            // For custom fields we need to remove the isDirty property
            customFields: (company.dirtyFields?.customFields || []).map(
              (fields: ICustomField[]) => _pick(fields, ['key', 'value']),
            ),
            dirtyFields: null,
          });
        });
        break;
      }
      case CANCEL_CONTACT_INLINE_EDIT: {
        const edited = model.filter((contact) => !!contact.dirtyFields);
        edited.toModelArray().forEach((contact) => {
          contact.update({
            dirtyFields: null,
          });
        });
        break;
      }

      case DELETE_CONTACT_SUCCESS: {
        // Note we dont use delete() as we want to keep the archived data in the orm store
        // We also archive the contacts related interactions
        const contact = model.withId(action.data.id);
        const archive = { isArchived: true };
        contact?.update(archive);
        // @ts-ignore - redux orm types
        contact?.interactionSet.update(archive);
        break;
      }
      case GET_REMINDERS_SUCCESS:
        action.data.forEach(
          ({ contact }: { contact: ContactUpsert }) =>
            contact && model.upsert(contact),
        );
        break;
    }

    return undefined;
  }

  toString() {
    return [this.firstName, this.lastName].join(' ');
  }

  toViewModel() {
    const status = getBracketByScore(this.relationship || 0);

    const personalStatus = getBracketByScore(this.usersRelationship || 0);

    return {
      ...this.ref,
      name: this.toString(),
      // company: this.companyId ? this.companyId.name : null,
      company: this.companyId,
      customFields: this.customFields || [],
      entityType: EntityType.CONTACT,
      relationship: this.relationship || 0,
      status: status?.status,
      statusString: status?.label,
      statusObject: status,
      statusObjectPersonal: personalStatus,
      roleTypeString: _get(
        language.role_type,
        this.roleType,
        language.role_type.default,
      ),
      referrer: this.referrerId,
      referrerString: this.referrerId
        ? `${this.referrerId.firstName} ${this.referrerId.lastName}`
        : null,
    };
  }

  toSimpleViewModel() {
    return {
      emails: this.emails,
      id: this.id,
      isArchived: this.isArchived,
      name: this.toString(),
      // company: this.companyId ? this.companyId.name : null,
      company: this.companyId,
      entityType: EntityType.CONTACT,
      relationship: this.relationship || 0,
      roleTypeString: _get(
        language.role_type,
        this.roleType,
        language.role_type.default,
      ) as string,
    };
  }
}

export type SimpleContactViewModel = ReturnType<Contact['toSimpleViewModel']>;

Contact.modelName = 'Contact';

Contact.fields = {
  id: attr(),
  accountId: attr(),
  companyId: fk('Company'),
  firstName: attr(),
  lastName: attr(),
  isArchived: attr(),
  role: attr(),
  roleType: attr(),
  emails: attr(),
  phone: attr(),
  timestamp: attr(),
  relationship: attr(),
  entityType: attr(),
  referrerId: fk('Contact'),
};

export type ContactViewModel = ReturnType<Contact['toViewModel']>;

export default Contact;
